import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api, { authAPI } from '../lib/api';
import { ArrowRight, Moon, ShieldCheck, Sun } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const afterLogin = (org) => {
  const back = sessionStorage.getItem('kv_return_to');
  sessionStorage.removeItem('kv_return_to');
  if (org?.id === 'platform') return back && back.startsWith('/platform-admin') ? back : '/platform-admin';
  return back && back.startsWith('/') && !back.startsWith('/login') ? back : '/dashboard';
};

export default function Login() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (sessionStorage.getItem('kv_practice_suspended')) { sessionStorage.removeItem('kv_practice_suspended'); sessionStorage.removeItem('kv_session_expired'); toast.error('This practice’s portal is temporarily unavailable. Please contact your accountant or platform support.'); return; } if (sessionStorage.getItem('kv_session_expired')) { sessionStorage.removeItem('kv_session_expired'); toast.info('Your session expired. Please sign in again – we’ll take you back to where you were.'); } }, []);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  // Forced MFA enrolment (org requires MFA and this user has none yet).
  const [mfaSetup, setMfaSetup] = useState(null); // {qr, secret}
  const [setupCode, setSetupCode] = useState('');
  const [pendingSession, setPendingSession] = useState(null); // {user, organization}

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Include the MFA code if we're on the second step.
      const payload = mfaRequired ? { ...data, mfa_code: mfaCode } : data;
      const response = await authAPI.login(payload);
      if (response.data.mfa_setup_required) {
        // Restricted step-up token: only /mfa/setup + /mfa/verify work with it.
        sessionStorage.setItem('access_token', response.data.access_token);
        setPendingSession({ user: response.data.user, organization: response.data.organization });
        const { data: setup } = await api.post('/api/mfa/setup');
        setMfaSetup(setup);
        toast.info('Your organisation requires two-factor authentication. Scan the QR code to continue.');
        return;
      }
      // Use sessionStorage for tab-isolated sessions (admin/client can coexist)
      sessionStorage.setItem('access_token', response.data.access_token);
      sessionStorage.setItem('user', JSON.stringify(response.data.user));
      sessionStorage.setItem('organization', JSON.stringify(response.data.organization));
      toast.success('Login successful!');
      navigate(afterLogin(response.data.organization));
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'MFA_REQUIRED') {
        // Password was correct — now ask for the authenticator code.
        setMfaRequired(true);
        toast.info('Enter the 6-digit code from your authenticator app');
      } else {
        toast.error((typeof detail === 'string' ? detail.replace(/^[A-Z_]+:\s*/, '') : detail) || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSetupVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/api/mfa/verify', { code: setupCode });
      if (data.access_token) {
        sessionStorage.setItem('access_token', data.access_token);
        sessionStorage.setItem('user', JSON.stringify(pendingSession.user));
        sessionStorage.setItem('organization', JSON.stringify(pendingSession.organization));
        toast.success('Two-factor authentication enabled');
        if (data.backup_codes) window.alert(`Save these one-time backup codes somewhere safe (shown only once):\n\n${data.backup_codes.join('\n')}`);
        navigate(afterLogin(pendingSession.organization));
      } else {
        toast.error('Could not complete setup — please sign in again.');
        setMfaSetup(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code — try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Form panel */}
      <div className="flex flex-col p-6 sm:p-10 lg:p-14">
        {/* Brand + theme toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center shadow-[0_0_18px_rgba(51,122,255,0.45)]">
              <span className="font-headings font-bold text-sm text-primary-foreground tracking-tight">PS</span>
            </div>
            <div className="leading-tight">
              <p className="font-headings font-bold text-base tracking-tight text-foreground">Practice Suite</p>
              <p className="font-data text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Tax &amp; Accounting</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            onClick={toggleTheme}
            aria-label="Toggle dark mode"
            data-testid="login-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center py-12">
          <div className="w-full max-w-md animate-fade-up">
            {mfaSetup ? (
              <div data-testid="mfa-setup-panel">
                <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-5 h-5 text-primary" strokeWidth={1.5} />
                </div>
                <h1 className="font-headings text-3xl font-bold tracking-tight text-foreground">Set up two-factor authentication</h1>
                <p className="text-sm text-muted-foreground mt-2 mb-8">
                  Your organisation requires MFA. Scan this QR code with Google Authenticator, Authy or any TOTP app, then enter the 6-digit code.
                </p>
                <div className="flex items-start gap-5">
                  <div className="bg-white p-2 rounded-md border border-border shrink-0">
                    <img src={mfaSetup.qr} alt="MFA QR code" className="w-36 h-36" data-testid="mfa-setup-qr" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground mb-1">Can't scan? Enter this key manually:</p>
                    <p className="font-data text-xs bg-muted rounded px-2 py-1.5 break-all select-all" data-testid="mfa-setup-secret">{mfaSetup.secret}</p>
                  </div>
                </div>
                <form onSubmit={handleMfaSetupVerify} className="mt-8 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="setup_code">Authentication Code</Label>
                    <Input
                      id="setup_code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="6-digit code"
                      value={setupCode}
                      onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
                      autoFocus
                      data-testid="mfa-setup-code-input"
                      className="h-11 bg-card font-data tracking-[0.4em] text-center focus-visible:ring-primary/40"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors"
                    disabled={loading || setupCode.length !== 6}
                    data-testid="mfa-setup-verify-button"
                  >
                    {loading ? 'Verifying…' : 'Enable & Sign In'}
                  </Button>
                </form>
              </div>
            ) : (
            <>
            <p className="font-data text-[11px] tracking-[0.2em] uppercase font-medium text-primary mb-3">Client &amp; Practice Workspace</p>
            <h1 className="font-headings text-4xl font-bold tracking-tight text-foreground">Welcome back</h1>
            <p className="text-sm text-muted-foreground mt-2 mb-10">Sign in to your account to continue.</p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  {...register('email')}
                  data-testid="login-email-input"
                  className="h-11 bg-card focus-visible:ring-primary/40"
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <a href="/forgot-password" className="text-xs underline text-muted-foreground" data-testid="login-forgot-password-link">Forgot password?</a>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  {...register('password')}
                  data-testid="login-password-input"
                  className="h-11 bg-card focus-visible:ring-primary/40"
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              {mfaRequired && (
                <div className="space-y-2">
                  <Label htmlFor="mfa_code">Authentication Code</Label>
                  <Input
                    id="mfa_code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                    data-testid="login-mfa-input"
                    className="h-11 bg-card font-data tracking-[0.4em] text-center focus-visible:ring-primary/40"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the code from your authenticator app (Google Authenticator, Authy…).
                  </p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors active:scale-[0.98] shadow-[0_0_24px_rgba(51,122,255,0.35)] group"
                disabled={loading || (mfaRequired && mfaCode.length !== 6)}
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : mfaRequired ? 'Verify & Sign In' : 'Sign In'}
                {!loading && <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" strokeWidth={1.5} />}
              </Button>
            </form>

            <div className="mt-8 text-center text-sm">
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link to="/register" className="text-primary hover:text-primary/80 font-medium underline-offset-4 hover:underline" data-testid="register-link">
                Register here
              </Link>
            </div>

            </>
            )}
          </div>
        </div>

        <p className="font-data text-[11px] text-muted-foreground">© {new Date().getFullYear()} Practice Suite</p>
      </div>

      {/* Futuristic visual panel */}
      <div className="hidden lg:block relative overflow-hidden bg-[#020817]">
        <div className="absolute inset-0 bg-grid-electric" />
        <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle,rgba(0,85,255,0.35)_0%,transparent_65%)] animate-glow-pulse" />
        <div className="absolute bottom-1/4 -left-40 w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle,rgba(0,213,255,0.18)_0%,transparent_65%)] animate-glow-pulse" style={{ animationDelay: '2.5s' }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020817] via-transparent to-transparent" />

        <div className="relative h-full flex flex-col justify-end p-14">
          <div className="mb-10 inline-flex items-center gap-2 self-start border border-[#337AFF]/30 bg-[#0055FF]/10 rounded-full px-4 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#337AFF] animate-pulse" />
            <span className="font-data text-[11px] uppercase tracking-[0.2em] text-[#99BFFF]">Built for Australian firms</span>
          </div>
          <p className="font-headings text-4xl font-bold tracking-tight text-white leading-tight max-w-lg">
            Every client, deadline and lodgement — in one intelligent workspace.
          </p>
          <div className="flex gap-12 mt-12">
            <div>
              <p className="font-data text-2xl font-bold text-[#669CFF]">BAS · IAS</p>
              <p className="text-xs text-white/50 mt-1.5">Lodgement tracking</p>
            </div>
            <div>
              <p className="font-data text-2xl font-bold text-[#669CFF]">AML/CTF</p>
              <p className="text-xs text-white/50 mt-1.5">Compliance built-in</p>
            </div>
            <div>
              <p className="font-data text-2xl font-bold text-[#669CFF]">Div 7A</p>
              <p className="text-xs text-white/50 mt-1.5">Loan calculators</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
